import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Layout from './components/layout/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import HomePage from './pages/HomePage'
import JoinTeamPage from './pages/JoinTeamPage'
import LoginPage from './pages/LoginPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import WorkspacePageV2 from './pages/WorkspacePageV2'

const DirectorDeskPage = lazy(() => import('./pages/DirectorDeskPage'))
import ArenaPage from './pages/arena/ArenaPage'
import ArenaRunPage from './pages/arena/ArenaRunPage'
import ArenaLeaderboardPage from './pages/arena/ArenaLeaderboardPage'
import ArenaAdminRoute from './components/arena/ArenaAdminRoute'
import AdminTopupRoute from './components/admin/AdminTopupRoute'
import AdminTopupPage from './pages/AdminTopupPage'
import AdminFreeTrialsPage from './pages/AdminFreeTrialsPage'
import AdminHubRoute from './components/admin/AdminHubRoute'
import AdminHubPage from './pages/AdminHubPage'
import BuiltinSkillsAdminRoute from './components/admin/BuiltinSkillsAdminRoute'
import AdminSkillsPage from './pages/AdminSkillsPage'
import AdminFlagsRoute from './components/admin/AdminFlagsRoute'
import AdminFlagsPage from './pages/AdminFlagsPage'
import OnboardingAdminRoute from './components/admin/OnboardingAdminRoute'
import AdminOnboardingPage from './pages/AdminOnboardingPage'
import PublishedProjectPage from './pages/PublishedProjectPage'
import ForAgentsPage from './pages/ForAgentsPage'
import BlogTeamsFeaturePage from './pages/BlogTeamsFeaturePage'
import LandingPageV2 from './pages/LandingPageV2'
import RetiredLandingRedirect from './components/landing/RetiredLandingRedirect'
import TemplatesGalleryPage from './pages/TemplatesGalleryPage'
import { TEMPLATES_GALLERY_ENABLED } from './constants/featureFlags'
import StyleLibraryTestPage from './pages/StyleLibraryTestPage'
import ProjectPlanModalTestPage from './pages/ProjectPlanModalTestPage'
import VideoTypeOptionsPreviewPage from './pages/VideoTypeOptionsPreviewPage'
import ConfigOptionsTestPage from './pages/ConfigOptionsTestPage'
import CameraCommandsPanelTestPage from './pages/CameraCommandsPanelTestPage'
import AccountRestorePage from './pages/AccountRestorePage'
import BannerHost from './components/banners/BannerHost'
import { BannerBoundary } from './components/banners/BannerBoundary'
import BannerAdminRoute from './components/admin/BannerAdminRoute'
import AdminBannersPage from './pages/AdminBannersPage'
import ApiKeysAdminRoute from './components/admin/ApiKeysAdminRoute'
import AdminApiKeysPage from './pages/AdminApiKeysPage'
import InternalModelsAdminRoute from './components/admin/InternalModelsAdminRoute'
import AdminInternalModelsPage from './pages/AdminInternalModelsPage'
import { useAppHealthAnalytics } from './analytics/useAppHealthAnalytics'
import SettingsModalProvider from './components/settings/settings-modal/SettingsModalProvider'
import SettingsModalPreviewPage from './components/settings/settings-modal/SettingsModalPreviewPage'
import CheckoutPage from './features/billing/CheckoutPage'
import CheckoutReturnPage from './features/billing/CheckoutReturnPage'
import { CHECKOUT_RETURN_PATHS } from './features/billing/checkoutReturnRoutes'
import { CheckoutPreviewPage } from './features/checkout/dreamwrite'
import { GenerationGracePeriodRuntime } from './features/generationGracePeriod/useGenerationGracePeriod'
import { NuxTooltipBoundary } from './onboarding/NuxTooltipBoundary'
import { NuxTooltipGate } from './onboarding/NuxTooltipGate'

/** Redirect old /shared/:token URLs — these no longer work without a version slug */
function SharedRedirect() {
  return <Navigate to="/" replace />
}

export default function App() {
  // App-health analytics: global uncaught-error + INP + session-started wiring
  // (RFC-0974 §5). Inert until consent + VITE_EVENTS_COLLECTOR_URL are set.
  useAppHealthAnalytics()
  const appBody = (
    <>
      <GenerationGracePeriodRuntime />
      <BannerBoundary>
        <BannerHost />
      </BannerBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPageV2 />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account/restore" element={<AccountRestorePage />} />
        {/* Retired landing URLs keep bookmarks working without shipping the old page. */}
        <Route path="/landing" element={<RetiredLandingRedirect />} />
        <Route path="/prototype" element={<RetiredLandingRedirect />} />
        <Route path="/prototype/index.html" element={<RetiredLandingRedirect />} />
        <Route path="/landing-v2" element={<LandingPageV2 />} />
        <Route path="/for-agents" element={<ForAgentsPage />} />
        <Route path="/blog/teams-feature" element={<BlogTeamsFeaturePage />} />
        {/* RFC-0934 P1 gallery — dark-launched (VITE_ENABLE_TEMPLATES_GALLERY). */}
        {TEMPLATES_GALLERY_ENABLED && (
          <Route path="/templates" element={<TemplatesGalleryPage />} />
        )}
        <Route path="/published/:projectToken/:versionSlug" element={<PublishedProjectPage />} />
        {/* Backward compat: old URLs without version slug */}
        <Route path="/shared/:shareToken" element={<SharedRedirect />} />
        {/* RFC-1371 §7.1 — where an invite link lands. PUBLIC on purpose: the
            token has to survive being signed out, and ProtectedRoute redirects
            to /login with no return path, which would drop the invitation.
            JoinTeamPage handles the signed-out case itself. */}
        <Route path="/join/:token" element={<JoinTeamPage />} />
        {import.meta.env.DEV && <Route path="/test/style-library" element={<StyleLibraryTestPage />} />}
        {import.meta.env.DEV && <Route path="/test/project-plan" element={<ProjectPlanModalTestPage />} />}
        {import.meta.env.DEV && <Route path="/test/video-type-options" element={<VideoTypeOptionsPreviewPage />} />}
        {import.meta.env.DEV && <Route path="/test/config-options" element={<ConfigOptionsTestPage />} />}
        {import.meta.env.DEV && <Route path="/test/camera-commands" element={<CameraCommandsPanelTestPage />} />}
        {import.meta.env.DEV && <Route path="/test/checkout" element={<CheckoutPreviewPage />} />}
        {/* Visual harness for the settings dialog — local dev has auth disabled,
            so the production entry point (the user menu) never renders. */}
        {import.meta.env.DEV && (
          <Route path="/test/settings-modal" element={<SettingsModalPreviewPage />} />
        )}

        {/* Protected routes - require authentication */}
        <Route element={<ProtectedRoute />}>
          {/* Sandbox credit purchase. Guarded inside the page on BOTH the
              @meta.com audience check and `billing_sandbox_enabled`, and it
              waits for those to resolve before redirecting. */}
          <Route path="/checkout" element={<CheckoutPage />} />
          {/* Where Meta's checkout host returns the browser to. These two paths
              are registered on the APNB app row (#3446) and are matched against
              a declared prefix, so they are an external contract — do not
              rename them without coordinating with APNB. */}
          <Route
            path={CHECKOUT_RETURN_PATHS.success}
            element={<CheckoutReturnPage outcome="success" />}
          />
          <Route
            path={CHECKOUT_RETURN_PATHS.failure}
            element={<CheckoutReturnPage outcome="failure" />}
          />
          {/* Canvas V2 workspace route */}
          <Route path="/project/:projectId/director-desk/:canvasId" element={<Suspense fallback={<div className="h-screen bg-background" />}><DirectorDeskPage /></Suspense>} />
          <Route path="/project/:projectId/workspace" element={<WorkspacePageV2 />} />
          <Route path="/project/:projectId/workspace/:canvasId" element={<WorkspacePageV2 />} />

          <Route element={<Layout />}>
            <Route path="dashboard" element={<HomePage />} />
            <Route path="settings/billing" element={<HomePage />} />
                  <Route path="settings/billing/add-credits" element={<HomePage />} />
            {/* RFC-0944: user rules moved into the "My rules" user-menu modal.
                Keep this protected redirect so bookmarks / the old link land on
                the dashboard with the modal auto-opened (Header reads ?rules=1). */}
            <Route path="settings/rules" element={<Navigate to="/dashboard?rules=1" replace />} />
            {/* Arena is internal/admin-only (verified @meta.com email). */}
            <Route element={<ArenaAdminRoute />}>
              <Route path="arena" element={<ArenaPage />} />
              <Route path="arena/:runId" element={<ArenaRunPage />} />
              <Route path="arena/leaderboard/:agentType" element={<ArenaLeaderboardPage />} />
            </Route>
            {/* Admin hub and credit top-up tools (admin or scoped operator permission). */}
            <Route element={<BuiltinSkillsAdminRoute />}>
              <Route path="admin/skills" element={<AdminSkillsPage />} />
            </Route>
            <Route element={<AdminHubRoute />}>
              <Route path="admin" element={<AdminHubPage />} />
            </Route>
            <Route element={<AdminTopupRoute />}>
              <Route path="admin/topup" element={<AdminTopupPage />} />
              <Route path="admin/free-trials" element={<AdminFreeTrialsPage />} />
            </Route>
            {/* Runtime feature flags (admin or flags_admin permission, RFC-0856 I7). */}
            <Route element={<AdminFlagsRoute />}>
              <Route path="admin/flags" element={<AdminFlagsPage />} />
            </Route>
            {/* Admin-only: manage dynamic DB-backed banners/notifications. */}
            <Route element={<BannerAdminRoute />}>
              <Route path="admin/banners" element={<AdminBannersPage />} />
            </Route>
            {/* Admin-only: mint/revoke per-user dwk_ API keys for REST access. */}
            <Route element={<ApiKeysAdminRoute />}>
              <Route path="admin/api" element={<AdminApiKeysPage />} />
            </Route>
            {/* White-glove partner onboarding (admin or onboarding_operator). */}
            <Route element={<OnboardingAdminRoute />}>
              <Route path="admin/onboarding" element={<AdminOnboardingPage />} />
            </Route>
            <Route element={<InternalModelsAdminRoute />}>
              <Route path="admin/internal-models" element={<AdminInternalModelsPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  )
  return (
    // SettingsModalProvider sits ABOVE <Routes> on purpose: the landing
    // surfaces ("/", "/landing-v2") are top-level routes OUTSIDE
    // <Layout>, so a provider mounted there could only ever serve the
    // dashboard. Hoisting also gives us one modal instance and one deep-link
    // parser for every surface instead of a copy per navbar.
    <SettingsModalProvider>
      <NuxTooltipBoundary fallback={appBody}>
        <NuxTooltipGate>{appBody}</NuxTooltipGate>
      </NuxTooltipBoundary>
    </SettingsModalProvider>
  )
}
